/* global $ */
import React, { useState, useMemo, useCallback } from 'react';
import axios from 'axios';
import { Form, Row, Col, Button, ButtonGroup, ToggleButton, ToggleButtonGroup, Card, IconButton } from '../../ui-kit';
import createToast from '../../../utils/toast';
import { IconCaretDown, IconCaretUp } from '../../ui-kit/icons';
import GenericAvailabilityCodeSelector from '../../general/AvailabilityCodeSelector';
import PeriodSelector from '../../../utils/forms/PeriodSelector';
import { roundTimeDown } from '../../../utils/time';
import Loader from '../../general/Loader';
import ScheduleWarningsPopoverConnected from '../warnings/ScheduleWarningsPopoverConnected';
import { isEmpty } from 'lodash';
import toSentence from '../../../utils/toSentence';
import moment from 'moment';

const AvailabilityCodeSelector = ({ availabilityCodes, availabilityCodesLoaded, updateAvailabilityCode }) => {
  if (isEmpty(availabilityCodes)) return null;

  return (
    <Form.Group as={Row} className="mb-3">
      <Form.Label column sm={3}>
        {I18n.t('activerecord.attributes.schedule/availability_code.short_model_name')}
      </Form.Label>
      <Col>
        <GenericAvailabilityCodeSelector
          loading={!availabilityCodesLoaded}
          availabilityCodes={availabilityCodes}
          onChange={updateAvailabilityCode}
          multi={false}
        />
      </Col>
    </Form.Group>
  );
};

const DetailedFormFields = ({
  currentUserPlannableStationMemberships,
  exceptionParams,
  setExceptionParams,
  saving,
  updateExceptionSchedule,
  availabilityCodes,
  availabilityCodesLoaded
}) => {
  const setMembershipsToUpdate = (membershipId, status) =>
    setExceptionParams({
      ...exceptionParams,
      membership_availability: {
        ...exceptionParams.membership_availability,
        [membershipId]: status
      }
    });

  const valid = !isEmpty(exceptionParams.membership_availability);

  const updateAvailabilityCode = availabilityCodes => {
    setExceptionParams({ ...exceptionParams, availability_code_id: availabilityCodes[0] });
  };

  const updateDatetimeFields = e =>
    setExceptionParams({ ...exceptionParams, start_time: e.start_time, end_time: e.end_time });

  const handleComments = e => setExceptionParams({ ...exceptionParams, comments: e.target.value });

  return (
    <>
      <div className="mb-2">
        <PeriodSelector
          startTime={exceptionParams.start_time}
          endTime={exceptionParams.end_time}
          onChange={updateDatetimeFields}
          minutesInterval={15}
          horizontal={false}
        />
      </div>
      <AvailabilityCodeSelector
        availabilityCodes={availabilityCodes}
        availabilityCodesLoaded={availabilityCodesLoaded}
        updateAvailabilityCode={updateAvailabilityCode}
      />
      <Form.Group as={Row} className="mb-3">
        <Col>
          <Form.Control
            as="textarea"
            placeholder={I18n.t('activerecord.attributes.schedule/schedule_exception.comments')}
            onChange={handleComments}
          />
        </Col>
      </Form.Group>
      {currentUserPlannableStationMemberships.map(membership => (
        <Row key={membership.id} className="mb-1 station-availability-selector">
          <Col className="d-flex align-items-center station-name">{membership.group.name}</Col>
          <Col className="d-flex justify-content-end">
            <ToggleButtonGroup
              type="radio"
              name={`availability_for_${membership.id}`}
              className="mb-2"
              onChange={e => setMembershipsToUpdate(membership.id, e)}
            >
              <ToggleButton
                id={`${membership.id}-on-button`}
                variant={
                  exceptionParams.membership_availability[membership.id] === true ? 'success' : 'outline-success'
                }
                value={true}
              >
                {I18n.t('check_in_abbr')}
              </ToggleButton>
              <ToggleButton
                id={`${membership.id}-off-button`}
                variant={exceptionParams.membership_availability[membership.id] == false ? 'danger' : 'outline-danger'}
                value={false}
              >
                {I18n.t('check_out_abbr')}
              </ToggleButton>
            </ToggleButtonGroup>
          </Col>
        </Row>
      ))}
      <Button className="w-100" variant="primary" disabled={saving || !valid} onClick={() => updateExceptionSchedule()}>
        {I18n.t('save')}
      </Button>
    </>
  );
};

const hourOptions = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23];
const minuteOptions = [0, 15, 30, 45];

const SimpleFormFields = ({
  exceptionParams,
  setExceptionParams,
  currentUserPlannableStationMemberships,
  saving,
  updateExceptionSchedule
}) => {
  const endHour = useMemo(() => moment(exceptionParams.end_time).hour(), [exceptionParams.end_time]);
  const endMinute = useMemo(() => moment(exceptionParams.end_time).minute(), [exceptionParams.end_time]);

  const setMinutes = useCallback(
    minute => {
      const newEndTime = moment(exceptionParams.end_time).minute(minute);
      setExceptionParams({ ...exceptionParams, end_time: newEndTime.format() });
    },
    [exceptionParams, setExceptionParams]
  );

  const setHours = useCallback(
    hour => {
      const newEndTime = moment(exceptionParams.end_time).hour(hour);
      setExceptionParams({ ...exceptionParams, end_time: newEndTime.format() });
    },
    [exceptionParams, setExceptionParams]
  );

  return (
    <>
      <div className="input-group input-group-lg mb-3">
        <select
          name="end_time_hours"
          className="form-control text-end hour-select"
          onChange={e => setHours(e.target.value)}
          value={endHour}
        >
          {hourOptions.map(hour => (
            <option key={hour} value={hour}>
              {hour}
            </option>
          ))}
        </select>
        <span className="input-group-text">:</span>
        <select
          name="end_time_minutes"
          className="form-control text-start minute-select"
          onChange={e => setMinutes(e.target.value)}
          value={endMinute}
        >
          {minuteOptions.map(minute => (
            <option key={minute} value={minute}>
              {minute.toString().padStart(2, '0')}
            </option>
          ))}
        </select>
      </div>
      <ButtonGroup className="w-100 mb-1">
        <Button variant="success" className="w-50" disabled={saving} onClick={() => updateExceptionSchedule(true)}>
          {I18n.t('check_in')}
        </Button>
        <Button variant="danger" className="w-50" disabled={saving} onClick={() => updateExceptionSchedule(false)}>
          {I18n.t('check_out')}
        </Button>
      </ButtonGroup>
      {currentUserPlannableStationMemberships.length > 1 && (
        <p className="text-center mb-0 small text-muted">
          <b>{I18n.t('quick_availability_form.applies_to')}</b>{' '}
          {toSentence(currentUserPlannableStationMemberships.map(m => m.group.name))}
        </p>
      )}
    </>
  );
};

const defaultValues = () => ({
  start_time: roundTimeDown(moment()).format(),
  end_time: roundTimeDown(moment().add(2, 'hours')).format(),
  channel: 'desktop',
  comments: '',
  membership_availability: {}
});

const AvailabilityChangeForm = ({
  availabilityCodes,
  availabilityCodesLoaded,
  currentUser,
  currentUserPlannableStationMemberships
}) => {
  const [showDetailedForm, setShowDetailedForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [exceptionParams, setExceptionParams] = useState(defaultValues());
  const [currentExceptionParams, setCurrentExceptionParams] = useState();

  const calculateParams = value => {
    if (showDetailedForm) return exceptionParams;

    // we're in the simple form which only has a single selector for the end
    // time. The start time is always "now" rounded down to the nearest 15
    // minutes and the end time is the selected time on the current day, if that
    // time is before the start time, it means the user wants to set the
    // availability for the next day so we add 1 day to the end time
    const startTime = roundTimeDown(moment());
    const endTime = startTime
      .clone()
      .hour(moment(exceptionParams.end_time).hour())
      .minute(moment(exceptionParams.end_time).minute())
      .second(0);
    if (endTime.isBefore(startTime)) endTime.add(1, 'day');

    // remove membership_availability from exceptionParams, this is needed if
    // we are switching from detailed to simple view since we only care about
    // the 'available'' param
    let { membership_availability, ...params } = exceptionParams;
    return {
      ...params,
      start_time: startTime.format(),
      end_time: endTime.format(),
      available: value
    };
  };

  const updateExceptionSchedule = async value => {
    const params = calculateParams(value);

    // register the params in case of schedule warnings so we can retry
    setCurrentExceptionParams(params);

    return await performAPICall(params, false);
  };

  const retryIgnoreWarnings = async () => await performAPICall(currentExceptionParams, true);

  const performAPICall = async (params, ignore_schedule_warnings) => {
    setSaving(true);

    params = { ...params, ignore_schedule_warnings };
    const url = `/api/v2/users/${currentUser.id}/schedule_exceptions`;
    try {
      await axios.post(url, params);
      // refresh the availability indicators to reflect the update
      $('.remote-panel.availability-indicator-wrapper').trigger('refresh');

      createToast({
        message: I18n.t('changes_saved_successfully'),
        type: 'success'
      });
      setExceptionParams(defaultValues());
      setCurrentExceptionParams(null);
      setShowDetailedForm(false);
    } finally {
      setSaving(false);
    }
  };

  if (!currentUser) return <Loader />;

  if (currentUser && isEmpty(currentUserPlannableStationMemberships))
    return (
      <span className="text-muted">
        <i>{I18n.t('dashboards.widget_archetypes.quick_availability_change.no_schedule')}</i>
      </span>
    );

  const FormFields = showDetailedForm ? DetailedFormFields : SimpleFormFields;

  return (
    <Card>
      <Card.Header>
        <Card.Title className="d-flex align-items-center">
          <>
            {I18n.t('check_in_or_out')}
            <div className="ms-auto">
              <IconButton
                className="toggle-detailed-mode"
                Icon={showDetailedForm ? IconCaretUp : IconCaretDown}
                onClick={() => setShowDetailedForm(!showDetailedForm)}
              />
            </div>
          </>
        </Card.Title>
      </Card.Header>
      <Card.Body>
        <ScheduleWarningsPopoverConnected
          handleUpdated={retryIgnoreWarnings}
          handleDismissed={() => setSaving(false)}
        />
        <FormFields
          availabilityCodes={availabilityCodes}
          availabilityCodesLoaded={availabilityCodesLoaded}
          currentUserPlannableStationMemberships={currentUserPlannableStationMemberships}
          exceptionParams={exceptionParams}
          setExceptionParams={setExceptionParams}
          saving={saving}
          showDetailedForm={showDetailedForm}
          setShowDetailedForm={setShowDetailedForm}
          updateExceptionSchedule={updateExceptionSchedule}
        />
      </Card.Body>
    </Card>
  );
};

export default AvailabilityChangeForm;
